import React, { FC, useCallback, useLayoutEffect, useState } from 'react';

import Xmark from '@gravity-ui/icons/Xmark';
import { Button, Icon, Label, Loader } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { shareApi } from 'client/api';
import { useCurrentAliceApp } from 'client/hooks/useCurrentAliceApp';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { UserEntity } from 'client/types/utils';
import { makeEmail } from 'client/utils/sharing';
import * as keyset from 'i18n/Access';

import styles from './SharingPage.module.scss';

const t = i18n(keyset);

interface Props {
    user: UserEntity;
}

interface SharingPageUsersListProps {
    handleSharingAvailable: (isAvailable: boolean) => void;
}

const User: React.FC<Props> = ({ user }) => {
    const aliceApp = useCurrentAliceApp()!;
    const [isDeleted, setIsDeleted] = useState(false);

    const [deleteShareHandler, state] = useFetchingHandler({
        handler: shareApi.deleteUserShare,
        onLoad: () => setIsDeleted(true),
    });

    const deleteShare = useCallback(async () => {
        await deleteShareHandler({ userId: user.uid, skillId: aliceApp.id }, aliceApp.channel);
    }, [aliceApp.id, aliceApp.channel, user.uid, deleteShareHandler]);

    return (
        <li className={styles.user}>
            {isDeleted ? (
                <div>{t('users-deleted')}</div>
            ) : (
                <>
                    <div>{makeEmail(user.login)}</div>
                    <Button title={t('deny-access')} onClick={deleteShare} view="flat" loading={state === 'loading'}>
                        <Icon data={Xmark} />
                    </Button>
                </>
            )}
        </li>
    );
};

export const SharingPageUsersList: FC<SharingPageUsersListProps> = ({ handleSharingAvailable }) => {
    const aliceApp = useCurrentAliceApp()!;
    const [users, setUsers] = useState<UserEntity[]>([]);

    const [fetchUsersHandler, state] = useFetchingHandler({
        handler: shareApi.getUserSharesList,
    });

    const fetchUsers = useCallback(async () => {
        const result = await fetchUsersHandler({ skillId: aliceApp.id }, aliceApp.channel);
        if (result?.users) {
            const users = result.users;
            setUsers(users);
        }

        if (result?.canBeShared === false) {
            handleSharingAvailable(false);
        } else {
            handleSharingAvailable(true);
        }
    }, [aliceApp.id, aliceApp.channel, fetchUsersHandler, handleSharingAvailable]);

    useLayoutEffect(() => {
        void fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aliceApp.id]);

    if (state === 'loading') {
        return (
            <div>
                <p className={styles.description}>{t('app-users-description')}</p>
                <div className={styles.loader}>
                    <Loader size="s" />
                </div>
            </div>
        );
    }

    if (state === 'error') {
        return (
            <div>
                <p className={styles.description}>{t('app-users-description')}</p>
                <div>
                    <Label theme="danger" size="m">
                        {t('error')}
                    </Label>
                </div>
                <Button onClick={fetchUsers} className={styles.reloadButton} size="l">
                    {t('users-button-reload')}
                </Button>
            </div>
        );
    }

    return (
        <div>
            <p className={styles.description}>{t('app-users-description')}</p>
            <div>
                {users.length > 0 ? (
                    <ul className={styles.usersList}>
                        {users.map((user) => (
                            <User user={user} key={user.login} />
                        ))}
                    </ul>
                ) : (
                    t('app-available-only-for-you')
                )}
            </div>
        </div>
    );
};
